import { Att, Attr, Category, FieldSchema, FkSchema, getFkSchema, isFieldSchema, Table, tableSchemaMap } from '@cumlaude/metadata';
import { allAttOverrides, AttOverride, AttOverrides } from '@cumlaude/shared-dashboard-registry';
import { groupBy, orderBy } from 'lodash-es';
import { isDateAttribute, isDefined } from '@cumlaude/shared-utils';
import { InstellingBron } from '@cumlaude/service-contract';

export function getFieldsByForeignKey(foreignKey: Attr) {
	for (const schema of Object.values(tableSchemaMap)) {
		const fkProp = schema.properties[foreignKey];
		if (fkProp === undefined) continue;

		const fkSchema = getFkSchema(fkProp);
		if (fkSchema) {
			return Object.entries(fkSchema.schema.properties)
				.filter(([, v]) => isFieldSchema(v))
				.map(([k]) => k);
		}
	}
	return undefined;
}

export type CategoryField = {
	att: string;
	category?: Category;
	label?: string;
	description?: string;
	filter: boolean;
	grouping: boolean;
	exportable: boolean;
	datePicker?: boolean;
	searchKeys?: string[];
	example?: string;
};

export type CategoryFields = {
	name: string;
	atts: CategoryField[];
};

/**
 * De override die voor dit attribuut geldt. Een sleutel zonder punt geldt ook voor alle subvelden
 * achter die foreign key, zodat een hele join in één regel aan- of uitgezet kan worden.
 */
export function findOverride(overrides: AttOverrides, att: string): AttOverride | undefined {
	return overrides[<Att>att] ?? overrides[<Att>att.split('.')[0]];
}

function applyOverride(field: CategoryField, overrides: AttOverrides): CategoryField {
	const override = findOverride(overrides, field.att);
	if (!override) return field;

	return { ...field, ...override };
}

/**
 * Een attribuut dat niet gefilterd, gegroepeerd én niet geëxporteerd mag worden, komt in geen enkele
 * lijst voor en laten we helemaal weg.
 */
function isVisible({ filter, grouping, exportable }: CategoryField): boolean {
	return filter || grouping || exportable;
}

/**
 * De attributen die op ten minste één dashboard filterbaar zijn gemaakt terwijl de yaml ze uit heeft
 * staan. Alleen de aan-kant telt mee: een dashboard dat een attribuut juist uitzet mag het niet uit de
 * lijst hieronder halen, want een ander dashboard kan het nog gewoon aanbieden.
 */
const ergensFilterbaar: AttOverrides = Object.fromEntries(
	allAttOverrides().flatMap((overrides) =>
		Object.entries(overrides)
			.filter(([, override]) => override?.filter === true)
			.map(([att]) => [att, { filter: true }])
	)
);

/**
 * Alle filters die in de applicatie kunnen voorkomen, over alle feitentabellen heen. Wordt bij het
 * opstarten door de FilterService gebruikt om van elk filter de configuratie te registreren, dus wat
 * hier ontbreekt bestaat later nergens meer.
 */
export function getAllFilters() {
	return Object.values(Table)
		.filter((table) => table.startsWith('fac'))
		.flatMap((table) => getFilters(table, ergensFilterbaar));
}

const searchKeysMap = new Map(getAllFilters().flatMap((cat) => cat.atts.map((field) => [field.att, field.searchKeys ?? []])));

export function getMetadataSearchKeys(att: string): string[] {
	return searchKeysMap.get(att) ?? [];
}

const datePickerMap = new Map(getAllFilters().flatMap((cat) => cat.atts.map((field) => [field.att, field.datePicker])));

/**
 * Of dit datumveld met een datumkiezer (periode) gefilterd wordt. Een datumveld met `datePicker: false`
 * in de metadata, zoals de geboortedatum, is een keuzelijst met losse datums.
 */
export function isDatePicker(att: string): boolean {
	return datePickerMap.get(att) ?? false;
}

function datePickerOf(att: string, schema: FieldSchema): boolean | undefined {
	return isDateAttribute(att) ? (schema.datePicker ?? true) : undefined;
}

export function getFilters(table: Table, overrides: AttOverrides = {}): CategoryFields[] {
	return getFromMetadata(table, undefined, overrides).map((categorie) => ({
		...categorie,
		atts: categorie.atts.filter((att) => att.filter),
	}));
}

export function getExportables(table: Table, exclude: (value: Att) => boolean, overrides: AttOverrides = {}): CategoryFields[] {
	return getFromMetadata(table, undefined, overrides).map((categorie) => ({
		...categorie,
		atts: categorie.atts.filter((att) => att.exportable && !exclude(<Att>att.att)),
	}));
}

export function getGroups(table: Table, overrides: AttOverrides = {}): CategoryFields[] {
	return getFromMetadata(table, undefined, overrides).map((categorie) => ({
		...categorie,
		atts: categorie.atts.filter((att) => att.grouping),
	}));
}

function getSubFieldLabel(subFieldSchema: FieldSchema, fkSchema: FkSchema, subKey: string): string | undefined {
	let label = subFieldSchema.label;

	const labelOverride = fkSchema.labelOverride?.[subKey];
	if (labelOverride) label = labelOverride;

	const labelSuffix = fkSchema.labelSuffix;
	if (labelSuffix) label = `${label} ${labelSuffix}`;

	return label;
}

function getSubFieldDescription(
	subFieldSchema: FieldSchema,
	fkSchema: FkSchema,
	subKey: string,
	bron: InstellingBron | undefined = undefined
): string | undefined {
	const descriptionOverride = fkSchema.descriptionOverride?.[subKey];
	const descriptionOverrideSomtoday = fkSchema['descriptionOverride-somtoday']?.[subKey];
	const descriptionOverrideMagister = fkSchema['descriptionOverride-magister']?.[subKey];

	if (bron === InstellingBron.Somtoday) {
		return descriptionOverrideSomtoday ?? descriptionOverride ?? subFieldSchema['description-somtoday'] ?? subFieldSchema.description;
	} else if (bron === InstellingBron.Magister) {
		return descriptionOverrideMagister ?? descriptionOverride ?? subFieldSchema['description-magister'] ?? subFieldSchema.description;
	} else {
		return descriptionOverride ?? subFieldSchema.description;
	}
}

function getForeignKeyField(
	fkSchema: FkSchema,
	subKey: string,
	subFieldSchema: FieldSchema,
	key: string,
	bron: InstellingBron | undefined = undefined,
	overrides: AttOverrides = {}
): CategoryField | undefined {
	if (fkSchema.exclude?.includes(subKey)) return undefined;
	if (fkSchema.include && !fkSchema.include.includes(subKey)) return undefined;

	const field = applyOverride(
		{
			att: key + '.' + subKey,
			category: fkSchema.categoryOverride?.[subKey] ?? subFieldSchema.category,
			label: getSubFieldLabel(subFieldSchema, fkSchema, subKey),
			description: getSubFieldDescription(subFieldSchema, fkSchema, subKey, bron),
			filter: subFieldSchema.filter ?? true,
			grouping: subFieldSchema.grouping ?? true,
			exportable: subFieldSchema.exportable ?? true,
			datePicker: datePickerOf(key + '.' + subKey, subFieldSchema),
			searchKeys: subFieldSchema.searchKeys,
			example: subFieldSchema.example,
		},
		overrides
	);

	return isVisible(field) ? field : undefined;
}

function getMetadataForForeignKey(
	key: string,
	fkSchema: FkSchema,
	bron: InstellingBron | undefined = undefined,
	overrides: AttOverrides = {}
): CategoryField[] {
	return Object.entries(fkSchema.schema.properties)
		.map(([subKey, subVal]) => {
			if (!isFieldSchema(subVal)) return undefined;
			return getForeignKeyField(fkSchema, subKey, subVal, key, bron, overrides);
		})
		.filter(isDefined);
}

export function getFromMetadata(table: Table, bron: InstellingBron | undefined = undefined, overrides: AttOverrides = {}): CategoryFields[] {
	const schema = tableSchemaMap[table];
	if (!schema) return [];

	const fields: CategoryField[] = [];
	for (const [key, val] of Object.entries(schema.properties)) {
		if (isFieldSchema(val)) {
			const field = applyOverride(
				{
					att: key,
					category: val.category,
					label: val.label,
					description: (bron === InstellingBron.Somtoday ? val['description-somtoday'] : val['description-magister']) ?? val.description,
					filter: val.filter ?? true,
					grouping: val.grouping ?? true,
					exportable: val.exportable ?? true,
					datePicker: datePickerOf(key, val),
					searchKeys: val.searchKeys,
					example: val.example,
				},
				overrides
			);
			if (isVisible(field)) fields.push(field);
		} else {
			const fkSchema = getFkSchema(val);
			if (fkSchema) fields.push(...getMetadataForForeignKey(key, fkSchema, bron, overrides));
		}
	}
	return orderBy(
		Object.entries(groupBy(orderBy(fields, 'label'), 'category')).map(([key, value]) => ({ name: key, atts: value })),
		'name'
	);
}
